import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useStore, formatPrice } from "@/lib/store";
import { t } from "@/lib/i18n";
import { optimizeImg } from "@/lib/img";

/**
 * Mobile-only sticky Add-to-Cart bar. Appears fixed at the bottom of the
 * viewport on PDP after the user scrolls past a small threshold, so a CTA is
 * always one thumb-tap away. Falls back to a "Velg størrelse" prompt that
 * scrolls the size selector into view if no variant is chosen yet.
 */
export default function StickyAddToCart({ product, variant, onSelectSize }) {
  const { addToCart } = useStore();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 320);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = () => {
    if (!variant?.available) {
      if (typeof onSelectSize === "function") onSelectSize();
      return;
    }
    addToCart(variant.id, 1);
  };

  const label = variant?.available ? t.pdp.stickyBuyNow : t.pdp.stickyPickSize;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.7, 0, 0.2, 1] }}
          className="fixed bottom-0 inset-x-0 z-40 md:hidden bg-white border-t border-[color:var(--marcini-border)] px-4 py-3 flex items-center gap-3 shadow-[0_-4px_20px_rgba(0,0,0,0.06)]"
          data-testid="sticky-add-to-cart"
        >
          <img
            src={optimizeImg(product.images[0], 160)}
            alt=""
            className="w-11 h-14 object-cover flex-shrink-0"
          />
          <div className="flex-1 min-w-0">
            <p className="font-serif-editorial text-sm leading-tight truncate">
              {product.color_name || product.title}
            </p>
            <p className="text-eyebrow text-[color:var(--marcini-fg-3)] mt-0.5">
              {formatPrice(variant?.price || product.price_range.min)}
              {variant?.title && ` · ${t.cart.size} ${variant.title}`}
            </p>
          </div>
          <button
            onClick={handleClick}
            className={`text-eyebrow px-5 py-3 whitespace-nowrap transition-colors ${
              variant?.available
                ? "bg-black text-white hover:bg-black/85"
                : "border border-black text-black"
            }`}
            data-testid="sticky-cta"
          >
            {label}
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
